import { ReactNode, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bot, UserPlus } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { INTELLIGENCE_PLANS } from "@/lib/intelligencePlans";
import { InviteLeaderDialog } from "./InviteLeaderDialog";

type CoachInviteFlowStep = "invite-question" | "bot-question";

interface CoachLeaderInviteFlowProps {
  trigger?: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  initialStep?: CoachInviteFlowStep;
  onInviteSent?: () => void;
  navigateOnSuccess?: boolean;
}

const botPlan = INTELLIGENCE_PLANS.intelligence;
const botHours = botPlan.monthlyCaptureMinutes / 60;

export function CoachLeaderInviteFlow({
  trigger,
  open,
  onOpenChange,
  initialStep = "bot-question",
  onInviteSent,
  navigateOnSuccess,
}: CoachLeaderInviteFlowProps) {
  const navigate = useNavigate();
  const [internalOpen, setInternalOpen] = useState(false);
  const [step, setStep] = useState<CoachInviteFlowStep>(initialStep);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const isControlled = typeof open === "boolean";
  const dialogOpen = isControlled ? open : internalOpen;

  useEffect(() => {
    if (dialogOpen) {
      setStep(initialStep);
    }
  }, [dialogOpen, initialStep]);

  const setPromptOpen = useCallback(
    (nextOpen: boolean) => {
      if (!isControlled) {
        setInternalOpen(nextOpen);
      }
      onOpenChange?.(nextOpen);
    },
    [isControlled, onOpenChange],
  );

  const closePrompt = useCallback(() => {
    setPromptOpen(false);
  }, [setPromptOpen]);

  const openInviteDialog = useCallback(() => {
    closePrompt();
    setInviteDialogOpen(true);
  }, [closePrompt]);

  const openPricing = useCallback(() => {
    closePrompt();
    navigate("/pricing");
  }, [closePrompt, navigate]);

  return (
    <>
      <Dialog open={dialogOpen} onOpenChange={setPromptOpen}>
        {trigger ? <DialogTrigger asChild>{trigger}</DialogTrigger> : null}
        <DialogContent className="max-w-lg">
          {step === "invite-question" ? (
            <>
              <DialogHeader>
                <DialogTitle>Invite your first leader?</DialogTitle>
                <DialogDescription>
                  This connects a leader to your coaching practice so Coach Veera can start building continuity
                  around their reflections, selected meetings, and coaching moments.
                </DialogDescription>
              </DialogHeader>

              <div className="rounded-lg border border-border bg-muted/35 p-4 text-sm text-muted-foreground">
                If you are still setting up your practice, you can skip this and come back from Leaders anytime.
              </div>

              <DialogFooter className="gap-2 sm:gap-0">
                <Button type="button" variant="ghost" onClick={closePrompt}>
                  Not now
                </Button>
                <Button type="button" onClick={() => setStep("bot-question")}>
                  <UserPlus className="mr-2 h-4 w-4" />
                  Yes, invite a leader
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Add Coach Vera Leadership Bot?</DialogTitle>
                <DialogDescription>
                  Coach Vera Leadership Bot can join selected meetings so real conversation becomes continuity
                  context for that leader. It is visible, optional, and not always on.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-3 rounded-lg border border-border bg-muted/35 p-4">
                <div className="flex items-start gap-3">
                  <Bot className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-foreground">
                      ${botPlan.priceUsdMonthly}/month per active leader
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Includes {botHours} capture hours for selected meetings and coaching sessions. You can invite
                      the leader now and add bot capture later once their seat exists.
                    </p>
                  </div>
                </div>
              </div>

              <DialogFooter className="gap-2 sm:gap-0">
                {initialStep === "invite-question" ? (
                  <Button type="button" variant="ghost" onClick={() => setStep("invite-question")}>
                    Back
                  </Button>
                ) : (
                  <Button type="button" variant="ghost" onClick={closePrompt}>
                    Not now
                  </Button>
                )}
                <Button type="button" variant="outline" onClick={openInviteDialog}>
                  Invite without bot
                </Button>
                <Button type="button" onClick={openPricing}>
                  See bot pricing
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <InviteLeaderDialog trigger={null} open={inviteDialogOpen} onOpenChange={setInviteDialogOpen} onInviteSent={onInviteSent} navigateOnSuccess={navigateOnSuccess} />
    </>
  );
}
